import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, switchMap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { FilterObject } from '../../interface/IFilterObj';
import { IListing, IListingState } from '../../interface/IListing';
import { IListingsResponse } from '../../interface/ilistings-response';
import { cleanObject } from '../../utilities/clean-object';
import { EnvironmentsService } from '../environments/environments.service';
import { LocationService } from '../location/location.service';

@Injectable({
  providedIn: 'root',
})
export class ListingsService {
  private listingsState: BehaviorSubject<IListingState>;
  public readonly listingsState$: Observable<IListingState>;
  private exactLocations: string[] = [];
  private normalizedLocations: string[] = [];
  private ukStagingApiUrl = 'https://api.staging.onlyusedtesla.co.uk';

  constructor(
    private http: HttpClient,
    private environmentService: EnvironmentsService,
    private locationService: LocationService
  ) {
    this.listingsState = new BehaviorSubject<IListingState>(
      this.getDefaultState()
    );
    this.listingsState$ = this.listingsState.asObservable();
    this.setCountry();
  }

  private getDefaultState(): IListingState {
    return {
      listingsFilter: null,
      listings: {
        search: { currentFilter: {}, filters: {}, listings: [] },
        account: { currentFilter: {}, filters: {}, listings: [] },
        featured: { currentFilter: {}, filters: {}, listings: [] },
        category: { currentFilter: {}, filters: {}, listings: [] },
      },
      skipListings: 0,
      sortListings: 'created DESC',
      totalListings: 0,
      currentListing: null,
      listingPending: false,
      filterPending: false,
    };
  }

  updateSkipListings(skipListings: number): void {
    this.updateState({ skipListings });
  }

  private decorateFilterObj(current: FilterObject) {
    delete current.OrderBy;
    delete current.Top;

    let skip = 0;

    if (current.Skip) {
      skip = current.Skip;
      delete current.Skip;
    }
    if (current.model) {
      current['vehicle.model'] = current.model;
      delete current.model;
    }
    if (current.year) {
      current['vehicle.year'] = Number(current.year);
      delete current.year;
    }
    if (current.trim) {
      current['vehicle.trim'] = current.trim;
      delete current.trim;
    }
    if (current.condition) {
      current['vehicle.condition'] = current.condition;
      delete current.condition;
    }
    if (current.autopilot) {
      current['vehicle.features.autopilot'] = current.autopilot;
      delete current.autopilot;
    }
    if (current.location) {
      current['location.state'] = current.location;
      delete current.location;
    }

    const cleanedFilter = cleanObject(current, [null, 0]);

    const params = Object.keys(cleanedFilter)
      .map((prop) => {
        const value = cleanedFilter[prop];

        // Ensure that `value` is a string before using `.includes()`
        if (typeof value === 'string') {
          const operator = value.includes('!') ? 'ne' : 'eq';
          return `${prop} ${operator} '${
            operator === 'ne' ? value.substring(1) : value
          }'`;
        }

        // Handle non-string values appropriately (for numbers, arrays, etc.)
        if (prop === 'vehicle.year') {
          return `${prop} eq ${value}`;
        }
        // If the value is not a string or an array, return the prop as is
        return `${prop} eq '${value}'`;
      })
      .filter(Boolean)
      .join();
    return { cleanedFilter, params, skip };
  }

  private setCountry(): void {
    // Retrieve and normalize locations
    this.exactLocations = this.locationService.getLocations();
    this.normalizedLocations = this.exactLocations.map((location) =>
      location.trim().toLowerCase()
    );
  }

  // Fetch filters
  fetchFilters(
    current: { [key: string]: string | number },
    resetSkip = false
  ): Observable<Record<string, Array<{ label: string; value: string }>>> {
    if (resetSkip) {
      this.updateState({ skipListings: 0 });
    }
    this.updateState({ filterPending: true });
    const query = this.decorateFilterObj({ ...current });
    return this.http
      .get<{ filters: Record<string, Array<{ name: string; count: number }>> }>(
        `${this.environmentService.url}/Listings/Filter?Filter=${query.params}`,
        {
          headers: new HttpHeaders({
            Accept: 'application/json;ver=1.1',
            Referer: 'https://onlyusedtesla.com/',
          }),
        }
      )
      .pipe(
        map(
          (res: {
            filters: Record<string, Array<{ name: string; count: number }>>;
          }) => {
            const totalCount = res.filters['make'][0].count;
            const filters = this.processFilters(res.filters);
            this.updateState({
              filterPending: false,
              listingsFilter: {
                filters: {} as Record<string, unknown>,
                currentFilter: {} as Record<string, unknown>,
                ...this.listingsState.value.listingsFilter,
                ...filters,
              },
              totalListings: totalCount,
            });
            return filters;
          }
        ),
        catchError((error) => {
          this.updateState({ filterPending: false });
          throw error;
        })
      );
  }
  // Fetch listings
  fetchListings(
    current: { [key: string]: string | number },
    type: 'search' | 'account' | 'featured' | 'category',
    allowSort = false
  ): Observable<IListingsResponse> {
    this.updateState({ listingPending: true });

    const query = this.decorateFilterObj({ ...current });
    let seperator = '';
    if (query.params.length > 0) {
      seperator = ',';
    }

    let url = `${this.environmentService.url}/Listings?Filter=location.country eq '${this.environmentService.country}'${seperator}${query.params}&Skip=${this.listingsState.value.skipListings}&Top=9`;

    if (allowSort) {
      url += `&OrderBy=${this.listingsState.value.sortListings}`;
    }
    return this.http
      .get<IListingsResponse>(url, {
        headers: new HttpHeaders({
          Accept: 'application/json;ver=1.1',
          Referer: 'https://onlyusedtesla.com/',
        }),
      })
      .pipe(
        map((listings) => {
          this.updateState({
            listingPending: false,
            listings: {
              ...this.listingsState.value.listings,
              [type]: listings,
            },
            skipListings:
              Number(query.skip) || this.listingsState.value.skipListings,
          });
          return listings;
        }),
        catchError((error) => {
          this.updateState({ listingPending: false });
          throw error;
        })
      );
  }

  fetchMyListings(): Observable<IListing[]> {
    return this.http.get<IListing[]>(
      `${this.environmentService.url}/Users/Me/Listings`,
      {
        headers: new HttpHeaders({
          Accept: 'application/json;ver=1.0',
          Referer: 'https://onlyusedtesla.com/',
        }),
      }
    );
  }

  // Fetch a single listing by ID
  fetchListing(id: string): Observable<IListing> {
    this.updateState({ listingPending: true });

    return this.http
      .get<IListing>(`${this.environmentService.url}/Listings/${id}`, {
        headers: new HttpHeaders({
          Accept: 'application/json;ver=1.0',
          Referer: 'https://onlyusedtesla.com/',
        }),
      })
      .pipe(
        map((listing) => {
          this.updateState({ listingPending: false, currentListing: listing });
          return listing;
        }),
        catchError((error) => {
          this.updateState({ listingPending: false });
          throw error;
        })
      );
  }

  // Update the internal state using BehaviorSubject
  private updateState(newState: Partial<IListingState>) {
    const currentState = this.listingsState.value;
    this.listingsState.next({ ...currentState, ...newState });
  }

  // Process filters
  private processFilters(
    filters: Record<string, Array<{ name: string; count: number }>>
  ): Record<string, Array<{ label: string; value: string }>> {
    const nameToLabel = (name: string) => {
      switch (name) {
        case 'ForSale':
          return 'For Sale';
        case 'LeaseTakeover':
          return 'Lease Takeover';
        case 'SalePending':
          return 'Pending Sale';
        case 'VeryGood':
          return 'Very Good';
        default:
          return name;
      }
    };

    const processedFilters: Record<
      string,
      Array<{ label: string; value: string }>
    > = {};

    for (const option in filters) {
      processedFilters[option] = filters[option]
        .filter((o) => Number(o.count) > 0 && o.name !== 'Sold')
        .map((option) => ({
          label: `${nameToLabel(option.name)} (${option.count})`,
          value: option.name,
        }));
    }

    return processedFilters;
  }

  fetchListingsByLocation(
    location: string
  ): Observable<IListingsResponse> | null {
    if (!location || typeof location !== 'string' || location.trim() === '') {
      console.log('Invalid location provided');
      return null;
    }

    const url = `${this.environmentService.url}/Listings?Filter=location.state eq '${location}'`;
    const httpOptions = {
      headers: new HttpHeaders({
        Accept: 'application/json;ver=1.1',
        Referer: 'https://onlyusedtesla.com/',
      }),
    };
    return this.http.get<IListingsResponse>(url, httpOptions);
  }

  setSortListings(sortValue: string) {
    this.updateState({ sortListings: sortValue });
  }

  setSkipListings(skip: number): void {
    const updatedState = { ...this.listingsState.value, skipListings: skip };
    this.listingsState.next(updatedState);
  }

  createListing(form: any): Observable<IListing> {
    return this.http
      .post<IListing>(`${this.environmentService.url}/Listings`, form)
      .pipe(
        switchMap((listing) =>
          this.createImages(listing.id).pipe(map(() => listing))
        )
      );
  }

  private createImages(listingId: string): Observable<any> {
    return this.http.post(`${this.environmentService.url}/Images`, {
      ListingId: listingId,
    });
  }

  updateImage(listingId: string, file: File): Observable<any> {
    if (!(file instanceof File)) {
      console.error('Invalid file:', file);
      throw new Error('The uploaded file is not a valid File object.');
    }

    const formData = new FormData();
    formData.append('image', file, file.name);

    return this.http.put(
      `${this.environmentService.url}/Images/${listingId}`, // Updated endpoint path
      formData,
      {
        headers: new HttpHeaders({
          Accept: 'application/json',
        }),
      }
    );
  }

  deleteImage(listingId: string, fileName: string): Observable<any> {
    return this.http.delete(
      `${this.environmentService.url}/Images/${listingId}/${fileName}`
    );
  }

  closeImageUpload(id: string): Observable<any> {
    return this.http.get(`${this.environmentService.url}/Images/${id}/close`);
  }
}
